import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingOptions,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    LineTypes,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { getDistanceBetweenPoints, getDistanceFromLine, getHitThreshold, scaledCoordinate } from '../drawing.helper'
import { HANDLER_RADIUS } from '../drawing.handler.constants'
import { primaryDrawingColor } from '../../../../assets/colors'

export type PathDrawingState = {
    points: Point[]
}

export type PathDrawingOptions = {
    showLineHead: boolean,

}

export type PathDrawingSettings = {
    lineType: LineTypes,
    lineColor: ColorValue,
    lineWidth: number
}

class PathDrawingTool extends DrawingBase<PathDrawingOptions, PathDrawingSettings, PathDrawingState> {
    slope: number = 0
    rotation = 0
    intercept = 0
    scaledCoordinates: Record<string, PointCoordinates> = {}

    constructor(options: DrawingOptions<PathDrawingOptions, PathDrawingSettings, PathDrawingState>) {
        super(options)
        this._state = {
            points: options.initialState.points || []
        }
    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        this.calculateLineAttributes()
        const { points } = this._state

        this.setLineAttributes(ctx)
        ctx.beginPath()
        if (points.length < 2) return
        const start = points[0]
        ctx.moveTo(this.scaledCoordinates[start.id].x, this.scaledCoordinates[start.id].y)
        for (let c = 1; c < points.length; c++) {
            const p = points[c]
            ctx.lineTo(this.scaledCoordinates[p.id].x, this.scaledCoordinates[p.id].y)
        }
        ctx.stroke()
        ctx.closePath()

        this.options.showLineHead && this.drawLineHead(ctx)

    };

    calculateLineAttributes() {
        const { points } = this._state
        if (!points.length) return

        points.forEach((p) => {
            let x = this.timeToCoordinate(p.x)
            let y = this.priceToCoordinate(p.y)

            if (x && y) {
                this.scaledCoordinates[p.id] = {
                    x: scaledCoordinate(x, this.horizontalPixelRatio),
                    y: scaledCoordinate(y, this.verticalPixelRatio)
                }
            }
        })
        const p1 = points[points.length - 2]
        const p2 = points[points.length - 1]
        if (p1 && p2) {

            let p1x = this.timeToCoordinate(p1.x)
            let p1y = this.priceToCoordinate(p1.y)
            let p2x = this.timeToCoordinate(p2.x)
            let p2y = this.priceToCoordinate(p2.y)

            if (p1x && p2x && p1y && p2y) {
                p1x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
                p1y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
                p2x = scaledCoordinate(p2x, this.horizontalPixelRatio) as Coordinate
                p2y = scaledCoordinate(p2y, this.verticalPixelRatio) as Coordinate
                this.slope = (p2y - p1y) / (p2x - p1x)
                this.intercept = p1y - this.slope * p1x
                this.rotation = Math.atan(this.slope)
            }
        }
    }

    setLineAttributes(ctx: CanvasRenderingContext2D, setLineType = true) {
        let lineDash: number[] = []
        const lineWidth = this._settings.lineWidth * window.devicePixelRatio
        if (this._settings.lineType === 'dashed') {
            lineDash = [Math.max(lineWidth * 3, 8 * window.devicePixelRatio), 3 * window.devicePixelRatio]
        } else if (this._settings.lineType === 'dotted') {
            lineDash = [lineWidth, lineWidth]
        }
        setLineType && ctx.setLineDash(lineDash)
        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.lineWidth = lineWidth
        ctx.strokeStyle = this._settings.lineColor.color
    }

    drawLineHead(ctx: CanvasRenderingContext2D) {
        const { points } = this._state

        const p1 = points[points.length - 2]
        const p2 = points[points.length - 1]
        if (!p1 || !p2) return

        const p1x = this.scaledCoordinates[p1.id].x
        const p2x = this.scaledCoordinates[p2.id].x
        const p2y = this.scaledCoordinates[p2.id].y
        const sign = p1x >= p2x ? 1 : -1

        const head = Math.max(10, (this._settings.lineWidth * window.devicePixelRatio) * 4)

        let angle1 = Math.PI / 4 - this.rotation
        if (Math.abs(this.rotation) === Math.PI / 2) {
            angle1 = this.rotation + Math.PI / 4
        }
        let x = p2x
        let y = p2y
        const head1x = x + sign * head * Math.cos(angle1)
        const head1y = y - sign * head * Math.sin(angle1)
        this.setLineAttributes(ctx, false)
        ctx.setLineDash([])
        ctx.beginPath()
        ctx.moveTo(x, y)
        ctx.lineTo(head1x, head1y)
        ctx.stroke()
        ctx.closePath()

        let angle2 = Math.PI / 4 + this.rotation
        if (Math.abs(this.rotation) === Math.PI / 2) {
            angle2 = -this.rotation + Math.PI / 4
        }
        const head2x = x + sign * head * Math.cos(angle2)
        const head2y = y + sign * head * Math.sin(angle2)


        ctx.beginPath()
        ctx.moveTo(x, y)
        ctx.lineTo(head2x, head2y)
        ctx.stroke()
        ctx.closePath()
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean, doubleClick?: boolean) => {
        let points = this._state.points
        const point: Point = {
            id: 'p' + (points.length + 1),
            x: time,
            y: price
        }
        const lastFixedPoint = points[points.length - 2]
        const lastPoint = points[points.length - 1]

        if (points.length === 0 || (preview && this.lastAddedPoint && this.lastAddedPoint?.id === lastPoint.id)) {
            points.push(point)
            if (!preview) return { ...point }
        } else {
            if (preview) {
                points.pop()
                const p = { ...lastPoint }
                p.x = time
                p.y = price
                points.push(p)

            } else {
                const cX = this.timeToCoordinate(lastFixedPoint.x)
                const cY = this.priceToCoordinate(lastFixedPoint.y)

                if (cX && cY) {
                    const d = getDistanceBetweenPoints(cX, coordinates.x, cY, coordinates.y)
                    if (d <= HANDLER_RADIUS) {
                        points.pop()
                        this.onDrawingEnd()
                        return { ...lastFixedPoint }
                    } else {
                        points.pop()
                        const p = { ...lastPoint }
                        p.x = time
                        p.y = price
                        points.push(p)
                        if (doubleClick) this.onDrawingEnd()
                        return { ...p }
                    }
                }
            }
        }
    }

    getHandles = (): Point[] => {
        return this._state.points
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const { points } = this._state
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        if (points.length < 2) return null
        const threshold = getHitThreshold(this.horizontalPixelRatio)

        let start = this.scaledCoordinates[points[0].id]

        for (let c = 1; c < points.length; c++) {
            const end = this.scaledCoordinates[points[c].id]
            let minX = start.x, maxX = end.x, minY = start.y, maxY = end.y

            if (maxX < minX) {
                minX = end.x
                maxX = start.x
            }

            if (maxY < minY) {
                minY = end.y
                maxY = start.y
            }

            const [slope, intercept] = this.getSlopAndIntercept(start.x, start.y, end.x, end.y)
            const distance = getDistanceFromLine(scaledX, scaledY, slope, intercept)
            // make cursor active if distance from the line is less than a threshold
            if (distance <= threshold) {
                return {
                    externalId: this.id,
                    zOrder: 'normal'
                }
            }
            start = end
        }

        return null
    }

    getSlopAndIntercept(x1: number, y1: number, x2: number, y2: number) {
        const slope = (y2 - y1) / (x2 - x1)
        const intercept = y1 - slope * x1
        return [slope, intercept]
    }

    updateHandles(points: Point[]) {
        const pointsObj = points.reduce((acc, p) => {
            return {
                ...acc,
                [p.id]: p
            }
        }, {} as Record<string, Point>)
        const newPoints: Point[] = []
        this._state.points.forEach((h) => {
            newPoints.push(pointsObj[h.id])
        })
        this._state.points = newPoints
    }

    updateHandle = (id: string, time: Time, price: number) => {
        this._state.points.forEach((p) => {
            if (p.id === id) {
                p.x = time
                p.y = price
            }
        })
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { points } = this._state
        if (points.length < 2) return []

        const sorted = [...points].sort((a, b) => a.y - b.y)
        const configs: LabelAxisViewConfig<number>[] = [
            {
                id: 'outer',
                p1: sorted[0].y,
                p2: sorted[sorted.length - 1].y,
                fill: true,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            }
        ]

        for (let c = 1; c < sorted.length - 1; c++) {
            configs.push({
                id: 'price-' + sorted[c].id,
                p1: sorted[c].y,
                p2: sorted[c].y,
                fill: false,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            })
        }
        return configs
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { points } = this._state
        if (points.length < 2) return []

        const sorted = [...points].sort((a, b) => (a.x as number) - (b.x as number))
        const configs: LabelAxisViewConfig<Time>[] = [
            {
                id: 'time',
                p1: sorted[0].x,
                p2: sorted[sorted.length - 1].x,
                fill: true,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            }
        ]

        for (let c = 1; c < sorted.length - 1; c++) {
            configs.push({
                id: 'time-' + sorted[c].id,
                p1: sorted[c].x,
                p2: sorted[c].x,
                fill: false,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            })
        }

        return configs
    }

    getAvailableSettings = (): DrawingSettingGroup<PathDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'lineColor',
                    label: 'Line Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: primaryDrawingColor,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineType',
                    label: 'Line Type',
                    type: SettingType.LINE_TYPE,
                    defaultValue: 'normal',
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): PathDrawingSettings {
        return { ...this._settings }
    }

    getDrawingType(): DrawingTypes {
        return this.options.showLineHead ? 'path' : 'polyLine'
    }

    getMiniSettings = () => {
        return ['lineColor', 'lineType', 'lineWidth'] as (keyof PathDrawingSettings)[]
    }
}

export default PathDrawingTool